// features/settings/pages/DiscountReasonsPage.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { SettingsModal } from '../components/SettingsModal';
import * as api from '../services/settingsApi';
import {
    Tag, Plus, Save, ArrowLeft, Trash2, Percent, DollarSign,
    ShieldAlert, Pencil,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

interface DiscountReason {
    id: string;
    name: string;
    type: 'percentage' | 'fixed';
    maxAmount: number | null;
    requiresApproval: boolean;
    isActive: boolean;
}

export function DiscountReasonsPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const [showForm, setShowForm] = useState(false);
    const [editingReason, setEditingReason] = useState<DiscountReason | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<DiscountReason | null>(null);

    const [form, setForm] = useState<{
        name: string;
        type: DiscountReason['type'];
        maxAmount: string;
        requiresApproval: boolean;
    }>({
        name: '', type: 'percentage', maxAmount: '', requiresApproval: false,
    });

    const { data: reasons, isLoading, refetch } = useQuery<DiscountReason[]>({
        queryKey: ['discount-reasons', user?.tenantId],
        queryFn: () => api.getDiscountReasons(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const saveMutation = useMutation({
        mutationFn: (payload: any) => editingReason
            ? api.updateDiscountReason(user!.tenantId, editingReason.id, payload)
            : api.createDiscountReason(user!.tenantId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['discount-reasons'] });
            addToast({ type: 'success', title: editingReason ? 'Updated' : 'Created' });
            closeForm();
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
    });

    const deleteMutation = useMutation({
        mutationFn: (reasonId: string) => api.deleteDiscountReason(user!.tenantId, reasonId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['discount-reasons'] });
            setDeleteConfirm(null);
            addToast({ type: 'success', title: 'Deleted' });
        },
    });

    const handleEdit = (reason: DiscountReason) => {
        setEditingReason(reason);
        setForm({
            name: reason.name,
            type: reason.type,
            maxAmount: reason.maxAmount ? String(reason.maxAmount) : '',
            requiresApproval: reason.requiresApproval,
        });
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingReason(null);
        setForm({ name: '', type: 'percentage', maxAmount: '', requiresApproval: false });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-14 h-14 rounded-2xl border-4 border-violet-200 border-t-violet-600" />
            </div>
        );
    }

    const activeReasons = (reasons || []).filter(r => r.isActive).length;
    const requiringApproval = (reasons || []).filter(r => r.requiresApproval).length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/30">
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate('/settings')} className="p-2 rounded-xl hover:bg-slate-100 transition-colors" type="button">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl shadow-lg shadow-violet-500/25">
                                <Tag className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">Discount Reasons</h1>
                                <p className="text-sm text-slate-500">
                                    {reasons?.length || 0} reason{(reasons?.length || 0) !== 1 ? 's' : ''} · {activeReasons} active · {requiringApproval} require approval
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => { closeForm(); setShowForm(true); }}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl hover:from-violet-600 hover:to-purple-700 transition-all font-semibold shadow-lg shadow-violet-500/25 text-sm"
                            type="button"
                        >
                            <Plus className="w-4 h-4" /> Add Reason
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        { label: 'Total Reasons', value: reasons?.length || 0, icon: Tag, gradient: 'from-violet-500 to-purple-600' },
                        { label: 'Active', value: activeReasons, icon: Tag, gradient: 'from-emerald-500 to-teal-600' },
                        { label: 'Need Approval', value: requiringApproval, icon: ShieldAlert, gradient: 'from-amber-500 to-orange-600' },
                        { label: 'Percentage Based', value: (reasons || []).filter(r => r.type === 'percentage').length, icon: Percent, gradient: 'from-blue-500 to-indigo-600' },
                    ].map((stat, i) => (
                        <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all duration-300">
                            <div className="flex items-start justify-between">
                                <div className="space-y-1">
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{stat.label}</p>
                                    <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value.toLocaleString()}</p>
                                </div>
                                <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.gradient} shadow-lg`}>
                                    <stat.icon className="w-5 h-5 text-white" />
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Empty State */}
                {(!reasons || reasons.length === 0) ? (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="text-center py-20 bg-white rounded-3xl border border-slate-200">
                        <div className="w-20 h-20 bg-violet-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <Tag className="w-10 h-10 text-violet-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-700">No discount reasons</h3>
                        <p className="text-slate-400 mt-2 max-w-md mx-auto">
                            Create preset discount reasons like Staff Discount, Promotional Offer, or Loyalty Reward.
                        </p>
                        <button onClick={() => { closeForm(); setShowForm(true); }}
                            className="mt-6 px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl hover:from-violet-600 transition-all font-semibold shadow-lg inline-flex items-center gap-2">
                            <Plus className="w-5 h-5" /> Add First Reason
                        </button>
                    </motion.div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(reasons || []).map((reason, i) => (
                            <motion.div key={reason.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                whileHover={{ y: -4 }}
                                className="group bg-white rounded-2xl border-2 border-slate-200 shadow-sm hover:shadow-lg hover:border-violet-300 transition-all duration-300 overflow-hidden">
                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${reason.type === 'percentage' ? 'bg-violet-100' : 'bg-amber-100'}`}>
                                                {reason.type === 'percentage' ? (
                                                    <Percent size={20} className="text-violet-600" />
                                                ) : (
                                                    <DollarSign size={20} className="text-amber-600" />
                                                )}
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-slate-900">{reason.name}</h3>
                                                <p className="text-xs text-slate-500 capitalize">{reason.type} discount</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {reason.requiresApproval && (
                                                <Badge variant="warning" size="sm"><ShieldAlert size={10} className="mr-1" />Approval</Badge>
                                            )}
                                            <Badge variant={reason.isActive ? 'success' : 'danger'} size="sm">
                                                {reason.isActive ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </div>
                                    </div>
                                    {reason.maxAmount && (
                                        <div className="p-3 bg-slate-50 rounded-xl mb-3">
                                            <p className="text-xs text-slate-400">Maximum Amount</p>
                                            <p className="text-lg font-bold text-slate-900">
                                                {reason.type === 'percentage' ? `${reason.maxAmount}%` : `₦${parseFloat(String(reason.maxAmount)).toLocaleString()}`}
                                            </p>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 pt-3 border-t border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEdit(reason)}
                                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors">
                                            <Pencil size={12} /> Edit
                                        </button>
                                        <button onClick={() => setDeleteConfirm(reason)}
                                            className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-rose-500 hover:bg-rose-50 rounded-lg transition-colors">
                                            <Trash2 size={12} /> Delete
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Create/Edit Modal */}
                <SettingsModal
                    open={showForm}
                    onClose={closeForm}
                    title={editingReason ? 'Edit Discount Reason' : 'Add Discount Reason'}
                    subtitle="Define a preset discount type"
                    icon={Tag}
                    footer={
                        <>
                            <Button variant="secondary" onClick={closeForm}>Cancel</Button>
                            <Button
                                leftIcon={<Save size={16} />}
                                onClick={() => saveMutation.mutate({
                                    ...form,
                                    maxAmount: form.maxAmount ? parseFloat(form.maxAmount) : undefined,
                                })}
                                isLoading={saveMutation.isPending}
                                disabled={!form.name}
                            >
                                {editingReason ? 'Update' : 'Save'}
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <Input label="Reason Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Staff Discount" required />
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">Discount Type</label>
                            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as DiscountReason['type'] })}
                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20">
                                <option value="percentage">Percentage (%)</option>
                                <option value="fixed">Fixed Amount (₦)</option>
                            </select>
                        </div>
                        <Input
                            label="Maximum Amount (optional)"
                            type="number"
                            value={form.maxAmount}
                            onChange={(e) => setForm({ ...form, maxAmount: e.target.value })}
                            hint={form.type === 'percentage' ? 'Maximum percentage allowed (e.g., 25 for 25%)' : 'Maximum fixed amount allowed'}
                        />
                        <label className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-xl">
                            <input type="checkbox" checked={form.requiresApproval} onChange={(e) => setForm({ ...form, requiresApproval: e.target.checked })}
                                className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                            <div>
                                <span className="text-sm font-medium text-slate-700">Requires Manager Approval</span>
                                <p className="text-xs text-slate-400">Discount will need approval before being applied</p>
                            </div>
                        </label>
                    </div>
                </SettingsModal>

                {/* Delete Confirmation */}
                <AnimatePresence>
                    {deleteConfirm && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
                                <div className="text-center">
                                    <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
                                        <Trash2 className="w-8 h-8 text-rose-500" />
                                    </div>
                                    <h2 className="text-xl font-bold text-slate-900">Delete "{deleteConfirm.name}"?</h2>
                                    <p className="text-sm text-slate-500 mt-2">This discount reason will be permanently removed.</p>
                                </div>
                                <div className="flex gap-3 mt-8">
                                    <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-5 py-3 border-2 border-slate-300 rounded-xl font-medium text-sm">Cancel</button>
                                    <button onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                                        className="flex-1 px-5 py-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl hover:from-rose-600 transition-all font-semibold text-sm shadow-lg shadow-rose-500/25">
                                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}